import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import {
  useGetPublicProfileQuery,
  useGetUserTradeStatsQuery,
} from 'store/services/profile/profileApi';
import { ProfileTab } from 'types/interfaces/profile';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import { Helmet } from '@dr.pogodin/react-helmet';
import { ContactUserModal } from 'components/ContactUserModal/ContactUserModal';
import SendPointsModal from 'components/SendPointsModal/SendPointsModal';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import BlockUserModal from 'components/BlockUserModal/BlockUserModal';
import Footer from 'components/Footer';
import ProfileCard from './components/ProfileCard/ProfileCard';
import StatsCard from './components/StatsCard/StatsCard';
import NewUserCard from './components/NewUserCard/NewUserCard';
import ProfileTabs from './components/ProfileTabs/ProfileTabs';
import TradeHistoryTab from './components/TradeHistoryTab/TradeHistoryTab';
import ProfileWantListTab from './components/ProfileWantListTab/ProfileWantListTab';
import ProfileHaveListTab from './components/ProfileHaveListTab/ProfileHaveListTab';
import ProfileRestrictedMessage from './components/ProfileRestrictedMessage/ProfileRestrictedMessage';
import VisitorStrip from './components/VisitorStrip/VisitorStrip';
import { useUnblockUserMutation } from 'store/services/block/blockApi';
import { toast } from 'react-toastify';
import { TradeSidebar, MobileTradeDrawer } from './components/TradeSidebar';
import { ProfileTradeSidebarProvider, useProfileTradeSidebar } from './context/ProfileTradeSidebarContext';
import { useProfilePointsSocket } from 'hooks/useProfilePointsSocket';
import { useIsElite } from 'hooks/useIsElite';
import TrophyCaseTab from './components/TrophyCaseTab/TrophyCaseTab';
import SidebarBadgeDisplay, { SidebarBadgeDisplayHandle } from './components/SidebarBadgeDisplay/SidebarBadgeDisplay';
import EliteUpsellBanner from './components/TrophyCaseTab/EliteUpsellBanner';
import { useGetUserBadgesQuery } from 'store/services/badge/badgeApi';
import './UserProfilePage.css';
import { useCardFilterState } from 'components/FilterPanel/cardFilterUrlState';
import { useDrawerLayout } from 'components/FilterPanel/useDrawerLayout';

// Get art_crop URL from avatar URL
const getArtCropUrl = (avatarUrl: string | null): string | null => {
  if (!avatarUrl) return null;

  // If it's already an art_crop URL, return as-is
  if (avatarUrl.includes('art_crop')) return avatarUrl;

  // Try to convert from other image formats to art_crop
  // Scryfall URL format: https://cards.scryfall.io/{size}/{...}
  if (avatarUrl.includes('cards.scryfall.io')) {
    // Replace size indicator with art_crop
    return avatarUrl
      .replace('/normal/', '/art_crop/')
      .replace('/small/', '/art_crop/')
      .replace('/large/', '/art_crop/')
      .replace('/png/', '/art_crop/')
      .replace('/border_crop/', '/art_crop/');
  }

  return avatarUrl;
};

// Inner component that uses the trade sidebar context
interface UserProfilePageInnerProps {
  username: string;
  isSlugRoute?: boolean;
}

export const UserProfilePageInner: React.FC<UserProfilePageInnerProps> = ({ username, isSlugRoute }) => {
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const { setReceiver, isOpen: isSidebarOpen, closeSidebar } = useProfileTradeSidebar();

  // Profile and stats data
  const {
    data: profile,
    isLoading: isProfileLoading,
    error: profileError,
  } = useGetPublicProfileQuery(username || '', {
    skip: !username,
  });

  const {
    data: stats,
    isLoading: isStatsLoading,
  } = useGetUserTradeStatsQuery(username || '', {
    skip: !username,
  });

  // Check if viewing own profile
  const isOwnProfile = currentUser?.username?.toLowerCase() === username?.toLowerCase();

  // Block state (viewer-scoped flags from the profile payload)
  const viewerHasBlocked = profile?.viewerHasBlocked === true;
  const isBlockedView = viewerHasBlocked || profile?.profileRestricted === true;

  const { isElite } = useIsElite(!currentUser);
  const { data: userBadges } = useGetUserBadgesQuery(profile?.id ?? 0, { skip: !profile?.id || isBlockedView });
  const totalEarnedBadges = userBadges?.length ?? 0;

  // Subscribe to real-time point updates for viewed profile
  useProfilePointsSocket({
    userId: profile?.id,
    username: username || '',
    enabled: !!currentUser && !isOwnProfile && !!profile?.id,
  });

  // Contact modal state
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isSendPointsOpen, setIsSendPointsOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [unblockUser] = useUnblockUserMutation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Profile sidebar collapse state (for when trade sidebar is open)
  const [isProfileCollapsed, setIsProfileCollapsed] = useState(false);
  const [userOverrideCollapse, setUserOverrideCollapse] = useState<boolean | null>(null);

  // Mobile menu state
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Track whether the tab was set via URL param to prevent the default-tab effect from overriding it
  const tabSetByUrl = useRef(false);
  const sidebarBadgeRef = useRef<SidebarBadgeDisplayHandle>(null);

  // Deep link: ?tab= support for direct tab navigation
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const tabMap: Record<string, ProfileTab> = {
      'trophy-case': 'trophy-case',
      'have-list': 'have',
      'want-list': 'want',
      // Trade-map share links land here (utils/tradeMapShare.ts). Only `tab` is
      // removed below; utm_* and the referral param stay for the visitor strip.
      history: 'history',
    };
    if (tabParam && tabMap[tabParam]) {
      tabSetByUrl.current = true;
      setActiveTab(tabMap[tabParam]);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('tab');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Mobile detection
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Auto-collapse profile sidebar when trade sidebar opens, restore when it closes
  useEffect(() => {
    // Don't auto-collapse on mobile (<=768px uses MobileTradeDrawer instead)
    if (window.innerWidth <= 768) return;

    if (isSidebarOpen) {
      if (userOverrideCollapse === null) {
        setIsProfileCollapsed(true);
      }
    } else {
      setIsProfileCollapsed(false);
      setUserOverrideCollapse(null);
    }
  }, [isSidebarOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle handler for manual expand/collapse
  const handleToggleProfileSidebar = () => {
    const newState = !isProfileCollapsed;
    setIsProfileCollapsed(newState);
    setUserOverrideCollapse(newState);
  };

  // Tab state - determine default tab
  // Default to 'want' for other profiles since that's where the trade action is
  const [activeTab, setActiveTab] = useState<ProfileTab>('want');

  // Set default tab based on profile type and stats
  useEffect(() => {
    // Skip if tab was already set via URL deep link (e.g., ?tab=trophy-case)
    if (tabSetByUrl.current) return;

    if (isOwnProfile) {
      // For own profile, show history if they have trades, otherwise want list
      if (stats && stats.completedTrades > 0) {
        setActiveTab('history');
      } else {
        setActiveTab('want');
      }
    } else {
      // For other profiles, always default to want list (where trade actions happen)
      setActiveTab('want');
    }
  }, [stats, isOwnProfile]);

  // Set receiver for trade sidebar when profile loads
  useEffect(() => {
    if (profile?.id && profile?.username) {
      setReceiver(profile.username, profile.id);
    }
  }, [profile?.id, profile?.username, setReceiver]);

  // Close any open trade sidebar when the profile becomes restricted by a block
  useEffect(() => {
    if (isBlockedView) {
      closeSidebar();
    }
  }, [isBlockedView, closeSidebar]);

  // Background image URL (art_crop from avatar)
  const backgroundUrl = useMemo(() => {
    return getArtCropUrl(profile?.avatarUrl || null);
  }, [profile?.avatarUrl]);

  // Handle sidebar navigation attempts (for unauthenticated users)
  const handleNavigationAttempt = () => {
    if (!currentUser) {
      setShowLoginModal(true);
    }
  };

  // Handle contact click
  const handleContactClick = () => {
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }
    setIsContactModalOpen(true);
  };

  // Handle send points click
  const handleSendPointsClick = () => {
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }
    setIsSendPointsOpen(true);
  };

  // Handle send cards click
  const handleSendCardsClick = () => {
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }
    if (username) {
      const params = new URLSearchParams({ searchType: 'user', search: username });
      navigate(`/sendcards?${params.toString()}`);
    }
  };

  // Handle block click (from the "..." menu)
  const handleBlockClick = () => {
    if (!currentUser) {
      setShowLoginModal(true);
      return;
    }
    setIsBlockModalOpen(true);
  };

  // Handle unblock click (from the "..." menu)
  const handleUnblockClick = async () => {
    if (!currentUser || !profile) return;
    try {
      await unblockUser({ userId: profile.id, username: profile.username }).unwrap();
      toast.success(`${profile.username} has been unblocked.`);
    } catch (error) {
      console.error('Failed to unblock user:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // The three list tabs mount the shared filter rail. The page reflows left of
  // it only while the full rail shows: on the Want List tab with the cart open
  // the rail is a strip beside the cart and nothing reflows (decision 5).
  const cardFilters = useCardFilterState();
  const [mobileFilterSheetOpen, setMobileFilterSheetOpen] = useState(false);
  const tabHasRail = activeTab === 'want' || activeTab === 'have' || activeTab === 'history';
  // Both right-hand drawers push the page root through one rule; the cart is
  // considered open only where it is actually rendered (see the TradeSidebar mount below).
  const cartRendered = !isOwnProfile && !!currentUser && isSidebarOpen && !isBlockedView;
  const layout = useDrawerLayout({ cartOpen: cartRendered, railMounted: !isBlockedView && tabHasRail });

  // Show error state
  if (profileError) {
    const is404 = profileError && typeof profileError === 'object' && 'status' in profileError && profileError.status === 404;
    return (
      <div className="user-profile-layout">
        {/* Raw Helmet: no canonical on a URL that does not resolve. */}
        <Helmet>
          <title>{is404 ? 'User Not Found - TradeMagic' : 'Error Loading Profile - TradeMagic'}</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="user-profile-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/profile', Component: <></> }}
            isPublicMode={true}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />
          <div className="user-profile-main-area">
            <div className="user-profile-error">
              <div className="error-icon">{is404 ? '🔍' : '⚠️'}</div>
              <h1 className="error-title">{is404 ? 'User Not Found' : 'Error Loading Profile'}</h1>
              <p className="error-message">
                {is404
                  ? `The user "${username}" does not exist.`
                  : 'There was an error loading this profile. Please try again later.'}
              </p>
            </div>
          </div>
        </div>
        <Footer />
        <LoginPromptModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          collectionOwnerUsername={username || ''}
        />
      </div>
    );
  }

  // Breadcrumbs
  const breadcrumbs = [
    { name: 'Home', url: 'https://trademagic.gg/' },
    { name: 'Profile', url: `https://trademagic.gg/profile/u/${username?.toLowerCase()}` },
  ];

  // Determine whether to show stats or new user card
  // Show stats card if there are ANY stats to display (completedTrades > 0)
  const showStatsCard = stats && stats.completedTrades > 0;

  // Thin-profile rule (GSC soft-404 ticket, decision 6): a profile with no
  // want cards and no public have list has nothing to index. The backend
  // profile meta endpoint applies the same rule for crawlers that are routed
  // there; this covers JS-rendering crawlers that reach the SPA. Undefined
  // stats (still loading, or failed) stay indexable: never noindex on a guess.
  const isThinProfile =
    stats !== undefined && stats.hasWantListItems === false && stats.hasPublicHaveList === false;

  return (
    <>
      <SEOHelmet
        title={profile ? `${profile.username}'s Profile - TradeMagic` : 'Profile - TradeMagic'}
        description={
          profile
            ? `View ${profile.username}'s trading profile on TradeMagic. See their trade history, want list, and trading stats.`
            : 'View trader profiles on TradeMagic.'
        }
        canonical={`https://trademagic.gg/profile/u/${username?.toLowerCase()}`}
        noindex={isThinProfile}
      />
      <BreadcrumbList breadcrumbs={breadcrumbs} />

      <div className="user-profile-layout">
        {/* Mobile Menu Overlay - click to close */}
        {isMobile && isMobileMenuOpen && (
          <div
            className="mobile-menu-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <div className="user-profile-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/profile', Component: <></> }}
            isPublicMode={true}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />

          <div className="user-profile-main-area">
            {/* Header */}
            <TopNavigationBar
              title="Profile"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />

            {/* Background */}
            {backgroundUrl && (
              <div
                className="user-profile-background"
                style={{ backgroundImage: `url(${backgroundUrl})` }}
              />
            )}

            {/* The push lands HERE, below the page's own header, so the header
                stays full-bleed while both drawers narrow the content region. */}
            <div
              className={`user-profile-content ${cartRendered ? 'sidebar-open' : ''} ${isProfileCollapsed ? 'profile-collapsed' : ''} ${layout.pushProps.className}`}
              style={layout.pushProps.style}
            >
              {/* Sidebar */}
              <aside className={`user-profile-sidebar ${isProfileCollapsed ? 'collapsed' : ''}`}>
                {isProfileCollapsed ? (
                  <div className="user-profile-sidebar-collapsed-strip">
                    <button
                      className="user-profile-sidebar-expand-btn"
                      onClick={handleToggleProfileSidebar}
                      aria-label="Expand profile sidebar"
                      title="Show profile"
                    >
                      {profile?.avatarUrl ? (
                        <img
                          src={profile.avatarUrl}
                          alt={profile.username}
                          className="user-profile-sidebar-collapsed-avatar"
                        />
                      ) : (
                        <div className="user-profile-sidebar-collapsed-avatar-placeholder">
                          {(profile?.username || '?')[0].toUpperCase()}
                        </div>
                      )}
                      <svg
                        className="user-profile-sidebar-expand-chevron"
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                      >
                        <path d="M5 3L9 7L5 11" stroke="#7b5cff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <>
                    <ProfileCard
                      profile={profile}
                      isLoading={isProfileLoading}
                      isOwnProfile={isOwnProfile}
                      showContactButton={profile?.showContactButtonOnProfile ?? false}
                      onContactClick={handleContactClick}
                      onSendPointsClick={handleSendPointsClick}
                      onSendCardsClick={handleSendCardsClick}
                      isViewerElite={isElite}
                      viewerHasBlocked={viewerHasBlocked}
                      hideActionButtons={isBlockedView}
                      onBlockClick={handleBlockClick}
                      onUnblockClick={handleUnblockClick}
                    />

                    {/* Stats Card or New User Card */}
                    {!isBlockedView && (showStatsCard ? (
                      <StatsCard stats={stats} isLoading={isStatsLoading} />
                    ) : (
                      <NewUserCard username={username || ''} />
                    ))}

                    {/* Sidebar Badge Display + Elite Upsell Banner */}
                    {!isBlockedView && profile?.id && (
                      <div className={`sidebar-trophy-wrapper ${isOwnProfile && !isElite && totalEarnedBadges >= 2 ? 'has-upsell' : ''}`}>
                        <SidebarBadgeDisplay
                          ref={sidebarBadgeRef}
                          userId={profile.id}
                          isOwnProfile={isOwnProfile}
                          onViewAll={() => { setActiveTab('trophy-case'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        />
                        {isOwnProfile && !isElite && (
                          <EliteUpsellBanner earnedCount={totalEarnedBadges} />
                        )}
                      </div>
                    )}

                    {isSidebarOpen && (
                      <button
                        className="user-profile-sidebar-collapse-btn"
                        onClick={handleToggleProfileSidebar}
                        aria-label="Collapse profile sidebar"
                        title="Hide profile"
                      >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path d="M9 3L5 7L9 11" stroke="#7b5cff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span>Collapse</span>
                      </button>
                    )}
                  </>
                )}
              </aside>

              {/* Main Content */}
              <main className="user-profile-main">
                <div className="user-profile-main-card">
                  {isBlockedView && profile ? (
                    <ProfileRestrictedMessage
                      userId={profile.id}
                      username={profile.username}
                      viewerHasBlocked={viewerHasBlocked}
                    />
                  ) : (
                    <>
                      {/* Signed-out visitors only, as the card's first row above the tabs
                          (round 3). currentUser is hydrated synchronously from storage
                          before the first render (authSlice), so a signed-in viewer never
                          sees this, not even for a frame. */}
                      {!currentUser && profile && <VisitorStrip username={profile.username} />}
                      <ProfileTabs
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                        showHaveList={isOwnProfile || stats?.hasPublicHaveList === true}
                      />

                      {activeTab === 'history' && username && (
                        <TradeHistoryTab
                          username={username}
                          mobileFilterSheet={{ open: mobileFilterSheetOpen, onOpenChange: setMobileFilterSheetOpen }}
                        />
                      )}

                      {activeTab === 'want' && username && (
                        <ProfileWantListTab
                          username={username}
                          isOwnProfile={isOwnProfile}
                          receiverPoints={profile?.points}
                          mobileFilterSheet={{ open: mobileFilterSheetOpen, onOpenChange: setMobileFilterSheetOpen }}
                        />
                      )}

                      {activeTab === 'have' && username && (
                        <ProfileHaveListTab
                          username={username}
                          isOwnProfile={isOwnProfile}
                          mobileFilterSheet={{ open: mobileFilterSheetOpen, onOpenChange: setMobileFilterSheetOpen }}
                        />
                      )}

                      {activeTab === 'trophy-case' && profile && (
                        <TrophyCaseTab
                          userId={profile.id}
                          username={profile.username}
                          isOwnProfile={isOwnProfile}
                          isElite={isOwnProfile ? isElite : (profile.isElite ?? false)}
                          onShowcaseAdded={() => sidebarBadgeRef.current?.triggerConfetti()}
                        />
                      )}
                    </>
                  )}
                </div>
              </main>
            </div>
          </div>
        </div>

        {/* Contact Modal */}
        {profile && (
          <ContactUserModal
            isOpen={isContactModalOpen}
            onClose={() => setIsContactModalOpen(false)}
            receiverId={profile.id}
            receiverUsername={profile.username}
            receiverAvatar={profile.avatarUrl || undefined}
          />
        )}

        {/* Send Points Modal */}
        {profile && (
          <SendPointsModal
            isOpen={isSendPointsOpen}
            onClose={() => setIsSendPointsOpen(false)}
            recipientId={profile.id}
            recipientUsername={profile.username}
          />
        )}

        {/* Login Prompt Modal */}
        <LoginPromptModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          collectionOwnerUsername={username || ''}
        />

        {/* Block Confirmation Modal */}
        {profile && (
          <BlockUserModal
            isOpen={isBlockModalOpen}
            onClose={() => setIsBlockModalOpen(false)}
            userId={profile.id}
            username={profile.username}
          />
        )}

        {/* Trade Sidebar - only shown when not viewing own profile and not blocked */}
        {!isOwnProfile && currentUser && !isBlockedView && (
          <>
            <TradeSidebar />
            <MobileTradeDrawer
              filtersButton={
                // The cart bar carries Filters wherever the tab mounts the mobile
                // filter chrome: want, history, and have unless the have list is
                // private (that branch renders a message and no chrome, so the
                // button would open nothing). Fails open while stats load.
                activeTab === 'want' ||
                activeTab === 'history' ||
                (activeTab === 'have' && stats?.hasPublicHaveList !== false)
                  ? { count: cardFilters.tokens.length, onOpen: () => setMobileFilterSheetOpen(true) }
                  : undefined
              }
            />
          </>
        )}

        <Footer />
      </div>
    </>
  );
};

// Wrapper component that provides the trade sidebar context
export const UserProfilePage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  if (!username) return null;
  return (
    <ProfileTradeSidebarProvider>
      <UserProfilePageInner username={username} />
    </ProfileTradeSidebarProvider>
  );
};

export default UserProfilePage;
